import React,{createContext,useContext,useState,useEffect,useRef} from 'react';
import {useAuth} from './AuthContext';
import * as dataService from '../dataService.js';
import {getCachedLibrary,saveCachedLibrary,queueMutation} from '../utils/database.js';

const SettingsContext=createContext();

export const useSettings=()=> {
  const context=useContext(SettingsContext);
  if (!context) throw new Error('useSettings must be used within a SettingsProvider');
  return context;
};

const defaultSettings={
  viewMode:'grid',sortBy:'name',sortOrder:'asc',itemsPerPage:50,showThumbnails:true,showTitles:true,fontSize:'medium',spacing:'normal',enableAnimations:true,autoBackup:true,backupInterval:'daily',smartCollections:[],onboardingDismissed:false
};

const makeCollectionId=()=> `smart-${Date.now().toString(36)}-${Math.random().toString(36).slice(2,7)}`;

export const SettingsProvider=({children})=> {
  const [settings,setSettings]=useState(defaultSettings);
  const settingsRef=useRef(defaultSettings);
  const [loading,setLoading]=useState(false);
  const {user,isAuthenticated}=useAuth();

  const applySettingsState=(next)=> {settingsRef.current=next;setSettings(next);};

  useEffect(()=> {
    let cancelled=false;
    const loadSettings=async ()=> {
      if (!isAuthenticated || !user?.id) {
        applySettingsState(defaultSettings);
        return;
      }

      setLoading(true);
      try {
        const cached=await getCachedLibrary(user.id);
        if (!cancelled && cached?.settings) applySettingsState({...defaultSettings,...cached.settings});

        if (navigator.onLine!==false) {
          const remote=await dataService.getSetting('userSettings',{silent:true});
          if (!cancelled && remote) {
            const merged={...defaultSettings,...remote};
            applySettingsState(merged);
            await saveCachedLibrary(user.id,{settings:merged});
          }
        }
      } catch (error) {
        if (!dataService.isOfflineError(error) && !error?.isOffline) console.error('Failed to load settings:',error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadSettings();
    return ()=> {cancelled=true;};
  },[isAuthenticated,user?.id]);

  const persistSettings=async (nextSettings)=> {
    if (!user?.id) return;
    await saveCachedLibrary(user.id,{settings:nextSettings});
    if (!isAuthenticated) return;

    if (navigator.onLine===false) {
      await queueMutation(user.id,{entity:'setting',operation:'set',targetId:'userSettings',payload:{key:'userSettings',value:nextSettings}});
      return;
    }

    try {
      await dataService.setSetting('userSettings',nextSettings,{silent:true});
    } catch (error) {
      if (dataService.isOfflineError(error) || error?.isOffline) {
        await queueMutation(user.id,{entity:'setting',operation:'set',targetId:'userSettings',payload:{key:'userSettings',value:nextSettings}});
      } else {
        throw error;
      }
    }
  };

  const updateSettings=async (patch)=> {
    const previous=settingsRef.current;
    const next={...previous,...patch};
    applySettingsState(next);
    try {await persistSettings(next);} catch (error) {
      console.error('Failed to save settings:',error);
      applySettingsState(previous);
      throw error;
    }
  };

  const updateSetting=async (key,value)=> updateSettings({[key]:value});

  const resetSettings=async ()=> {
    const next={...defaultSettings};
    applySettingsState(next);
    await persistSettings(next);
  };

  const saveSmartCollection=async (collection)=> {
    const cleaned={
      id: collection.id || makeCollectionId(),
      name: String(collection.name || 'Saved Search').trim() || 'Saved Search',
      query: String(collection.query || '').trim(),
      folderId: collection.folderId || 'ALL',
      tags: Array.isArray(collection.tags) ? collection.tags.map(tag=> String(tag).trim()).filter(Boolean) : [],
      createdAt: collection.createdAt || new Date().toISOString()
    };
    const existing=settingsRef.current.smartCollections || [];
    const next=existing.some(item=> item.id===cleaned.id) ? existing.map(item=> item.id===cleaned.id ? cleaned : item) : [...existing,cleaned];
    await updateSetting('smartCollections',next);
    return cleaned;
  };

  const deleteSmartCollection=async (id)=> {
    await updateSetting('smartCollections',(settingsRef.current.smartCollections || []).filter(item=> item.id!==id));
  };

  return (
    <SettingsContext.Provider value={{settings,updateSetting,updateSettings,resetSettings,saveSmartCollection,deleteSmartCollection,loading,defaultSettings}}>
      {children}
    </SettingsContext.Provider>
  );
};